import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import EventCalendar from '../EventCalendar/EventCalendar';
import { useLanguage } from '../../context/LanguageContext';
import { useOverlayBack } from '../../hooks/useSmartBack';
import { formatDateISO } from '../../utils/date';
import './EventCalendarFab.scss';

const toDayKey = (iso) => {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const EventCalendarFab = ({ events = [], dateFrom = '', dateTo = '', onSelectDay, onClearDay }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const calendarRef = useRef(null);

  const closeMenu = useCallback(() => setOpen(false), []);
  useOverlayBack(open, closeMenu, 'event-calendar');

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e && e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open ]);

  const selectedKey = useMemo(() => {
    if (dateFrom && dateTo && dateFrom === dateTo) return toDayKey(dateFrom);
    return null;
  }, [dateFrom, dateTo]);

  const isDayActive = selectedKey !== null;

  const selectedLabel = useMemo(() => {
    if (!isDayActive) return '';
    const d = new Date(selectedKey);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }, [isDayActive, selectedKey]);

  const handleDaySelect = (date) => {
    const iso = formatDateISO(date);
    if (!iso) return;
    if (dateFrom === iso && dateTo === iso) {
      if (onClearDay) onClearDay();
    } else if (onSelectDay) {
      onSelectDay(iso);
    }
  };

  // Render via portal so the fixed FAB stays viewport-anchored (like the
  // contact-center / AI FABs) even if a page ancestor uses transform/filter.
  const fabNode = (
    <div className="event-cal-fab" ref={rootRef}>
      {open && (
        <div
          className="event-cal-fab__panel"
          role="dialog"
          aria-label={t('dashboard.eventCalendar')}
        >
          <div className="event-cal-fab__header">
            <span className="event-cal-fab__title">
              <FaCalendarAlt aria-hidden="true" />
              {t('dashboard.eventCalendar')}
            </span>
            <span className="event-cal-fab__nav" role="group" aria-label="Calendar navigation">
              <button
                type="button"
                className="event-cal-fab__nav-btn"
                onClick={() => calendarRef.current?.goMonth(-1)}
                aria-label="Previous month"
              >
                <FaChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                className="event-cal-fab__today-btn"
                onClick={() => calendarRef.current?.goToday()}
              >
                {t('dashboard.today')}
              </button>
              <button
                type="button"
                className="event-cal-fab__nav-btn"
                onClick={() => calendarRef.current?.goMonth(1)}
                aria-label="Next month"
              >
                <FaChevronRight aria-hidden="true" />
              </button>
              <button
                type="button"
                className="event-cal-fab__close"
                onClick={() => setOpen(false)}
                aria-label="Close calendar"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </span>
          </div>

          <div className="event-cal-fab__body">
            <EventCalendar
              ref={calendarRef}
              events={events}
              selectedKey={selectedKey}
              onDaySelect={handleDaySelect}
            />
          </div>

          <div className="event-cal-fab__footer">
            {isDayActive ? (
              <>
                <span className="event-cal-fab__active-day">{selectedLabel}</span>
                <button
                  type="button"
                  className="event-cal-fab__clear"
                  onClick={() => {
                    if (onClearDay) onClearDay();
                  }}
                >
                  {t('common.clear')}
                </button>
              </>
            ) : (
              <span className="event-cal-fab__hint">{t('dashboard.calendarHint')}</span>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        className={`event-cal-fab__button${open ? ' event-cal-fab__button--active' : ''}${isDayActive ? ' event-cal-fab__button--filtered' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        title={open ? 'Close calendar' : t('dashboard.eventCalendar')}
        aria-label={open ? 'Close calendar' : 'Open event calendar'}
        aria-expanded={open}
      >
        {open ? <FaTimes aria-hidden="true" /> : <FaCalendarAlt aria-hidden="true" />}
        {isDayActive && !open && <span className="event-cal-fab__dot" aria-hidden="true" />}
      </button>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(fabNode, document.body);
  }
  return fabNode;
};

export default EventCalendarFab;
